// src/components/InvoiceDetails.jsx
import React, { useEffect, useMemo } from "react";
import Select from "./ui/Select";
import { iranCustoms } from "../constants/iranCustoms";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 12,
    borderColor: state.isFocused ? "#3b82f6" : "#e5e7eb", // blue-500 vs gray-200
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59,130,246,0.15)" : "none",
    ":hover": { borderColor: state.isFocused ? "#3b82f6" : "#d1d5db" },
    backgroundColor: "white",
    direction: "rtl",
    fontFamily: "inherit",
    
  }),
  valueContainer: (base) => ({ ...base, paddingInline: 12 }),
  placeholder: (base) => ({ ...base, color: "#9ca3af" }), // gray-400
  singleValue: (base) => ({ ...base, direction: "rtl" }),
  multiValue: (base) => ({
    ...base,
    borderRadius: 8,
    backgroundColor: "#eff6ff", // blue-50
  }),
  multiValueLabel: (base) => ({ ...base, color: "#1e40af" }), // blue-800
  multiValueRemove: (base) => ({
    ...base,
    ":hover": { backgroundColor: "#dbeafe", color: "#1e40af" }, // blue-100
  }),
  menu: (base) => ({ ...base, zIndex: 50, direction: "rtl" }),
};

const inputClass =
  "w-full rounded-xl border border-gray-200 px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500";

const groupClass = "flex flex-col gap-1.5";
const labelClass = "text-sm font-medium text-gray-700";

const InvoiceDetails = ({
  invoice,
  onFieldChange,
  sellerOptions,
  buyerOptions,
  currencyOptions,
  termsOfDeliveryOptions,
  meansOfTransportOptions,
  countryOptions,
  cottageOptions,
  proformaOptions,
}) => {
  // Preselect proforma from proforma_details if empty
  useEffect(() => {
    if (!invoice.proforma && invoice.proforma_details?.prfVCodeInt != null) {
      onFieldChange("proforma", String(invoice.proforma_details.prfVCodeInt));
    }
  }, [invoice.proforma, invoice.proforma_details, onFieldChange]);

  const iranCustomsOptions = useMemo(
    () =>
      iranCustoms.map((c) => ({
        value: c.ctmNameStr,
        label: `${c.ctmNameStr} (${c.ctmVCodeInt})`,
      })),
    []
  );

  // Helpers to map dashed strings <-> react-select values for multi
  const toMultiValue = (str, options) =>
    str
      ? str
          .split("-")
          .map((v) => options.find((o) => o.value === v.trim()))
          .filter(Boolean)
      : [];

  const fromMultiValue = (selected) =>
    (selected || []).map((o) => o.value).join("-");

  return (
    <div dir="rtl" className="font-vazir">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-xl md:text-2xl font-bold">جزئیات فاکتور</h3>
      </div>

      {/* Card */}
      <div className="rounded-2xl border bg-white shadow-sm">
        {/* Card header */}
        <div className="border-b px-4 md:px-6 py-4">
          <p className="text-sm text-gray-600">
            لطفاً اطلاعات فاکتور را با دقت تکمیل کنید.
          </p>
        </div>

        {/* Card content */}
        <div className="px-4 md:px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* ثبت سفارش */}
            <div className={groupClass}>
              <label htmlFor="proforma" className={labelClass}>
                ثبت سفارش
              </label>
              <Select
                inputId="proforma"
                classNamePrefix="rs"
                styles={selectStyles}
                options={proformaOptions}
                value={
                  proformaOptions.find(
                    (o) =>
                      String(o.value) ===
                      String(invoice.proforma !== "" && invoice.proforma != null
                        ? invoice.proforma
                        : invoice.proforma_details?.prfVCodeInt || "")
                  ) || null
                }
                onChange={(opt) =>
                  onFieldChange("proforma", opt ? opt.value : "")
                }
                placeholder="-- انتخاب ثبت سفارش --"
              />
            </div>

            {/* شماره اظهارنامه */}
            <div className={groupClass}>
              <label htmlFor="cottage" className={labelClass}>
                شماره اظهارنامه
              </label>
              <Select
                inputId="cottage"
                classNamePrefix="rs"
                styles={selectStyles}
                options={cottageOptions}
                value={
                  cottageOptions.find(
                    (o) => String(o.value) === String(invoice.cottage)
                  ) ||
                  null
                }
                onChange={(opt) =>
                  onFieldChange("cottage", opt ? opt.value : "")
                }
                placeholder="-- انتخاب شماره اظهارنامه --"
              />
            </div>

            {/* فروشنده */}
            <div className={groupClass}>
              <label htmlFor="seller" className={labelClass}>
                فروشنده
              </label>
              <Select
                inputId="seller"
                classNamePrefix="rs"
                styles={selectStyles}
                options={sellerOptions}
                value={
                  sellerOptions.find((o) => o.value === invoice.seller) || null
                }
                onChange={(opt) =>
                  onFieldChange("seller", opt ? opt.value : "")
                }
                placeholder="-- انتخاب فروشنده --"
              />
            </div>

            {/* خریدار */}
            <div className={groupClass}>
              <label htmlFor="buyer" className={labelClass}>
                خریدار
              </label>
              <Select
                inputId="buyer"
                classNamePrefix="rs"
                styles={selectStyles}
                options={buyerOptions}
                value={
                  buyerOptions.find((o) => o.value === invoice.buyer) || null
                }
                onChange={(opt) => onFieldChange("buyer", opt ? opt.value : "")}
                placeholder="-- انتخاب خریدار --"
              />
            </div>

            {/* شماره فاکتور */}
            <div className={groupClass}>
              <label htmlFor="invoice_number" className={labelClass}>
                شماره فاکتور
              </label>
              <input
                type="text"
                id="invoice_number"
                value={invoice.invoice_number || ""}
                onChange={(e) =>
                  onFieldChange("invoice_number", e.target.value)
                }
                placeholder="شماره فاکتور را وارد کنید"
                required
                className={inputClass}
              />
            </div>

            {/* تاریخ فاکتور */}
            <div className={groupClass}>
              <label htmlFor="invoice_date" className={labelClass}>
                تاریخ فاکتور
              </label>
              <input
                type="date"
                id="invoice_date"
                value={invoice.invoice_date || ""}
                onChange={(e) => onFieldChange("invoice_date", e.target.value)}
                required
                className={inputClass}
              />
            </div>

            {/* ارز */}
            <div className={groupClass}>
              <label htmlFor="invoice_currency" className={labelClass}>
                ارز
              </label>
              <Select
                inputId="invoice_currency"
                classNamePrefix="rs"
                styles={selectStyles}
                options={currencyOptions}
                value={
                  currencyOptions.find(
                    (o) => o.value === invoice.invoice_currency
                  ) || null
                }
                onChange={(opt) =>
                  onFieldChange("invoice_currency", opt ? opt.value : "")
                }
                placeholder="انتخاب ارز"
              />
            </div>

            {/* هزینه حمل */}
            <div className={groupClass}>
              <label htmlFor="freight_charges" className={labelClass}>
                هزینه حمل
              </label>
              <input
                type="number"
                id="freight_charges"
                value={invoice.freight_charges ?? ""}
                onChange={(e) =>
                  onFieldChange("freight_charges", e.target.value)
                }
                placeholder="هزینه حمل"
                className={inputClass}
                onWheel={(e) => e.target.blur()}
              />
            </div>

            {/* شماره مشتری */}
            <div className={groupClass}>
              <label htmlFor="customer_tel" className={labelClass}>
                شماره مشتری
              </label>
              <input
                type="text"
                id="customer_tel"
                value={invoice.customer_tel || ""}
                onChange={(e) => onFieldChange("customer_tel", e.target.value)}
                placeholder="شماره تماس مشتری را وارد کنید"
                className={inputClass}
              />
            </div>

            {/* کشور مبدأ */}
            <div className={groupClass}>
              <label htmlFor="country_of_origin" className={labelClass}>
                کشور مبدأ
              </label>
              <Select
                inputId="country_of_origin"
                classNamePrefix="rs"
                styles={selectStyles}
                options={countryOptions}
                isMulti
                value={toMultiValue(invoice.country_of_origin, countryOptions)}
                onChange={(opts) =>
                  onFieldChange("country_of_origin", fromMultiValue(opts))
                }
                placeholder="انتخاب کشور مبدأ"
              />
            </div>

            {/* کشور/بندر بارگیری */}
            <div className={groupClass}>
              <label htmlFor="port_of_loading" className={labelClass}>
                کشور بارگیری
              </label>
              <Select
                inputId="port_of_loading"
                classNamePrefix="rs"
                styles={selectStyles}
                options={countryOptions}
                isMulti
                value={toMultiValue(invoice.port_of_loading, countryOptions)}
                onChange={(opts) =>
                  onFieldChange("port_of_loading", fromMultiValue(opts))
                }
                placeholder="انتخاب بندر بارگیری"
              />
            </div>

            {/* شرایط تحویل */}
            <div className={groupClass}>
              <label htmlFor="terms_of_delivery" className={labelClass}>
                شرایط تحویل
              </label>
              <Select
                inputId="terms_of_delivery"
                classNamePrefix="rs"
                styles={selectStyles}
                options={termsOfDeliveryOptions}
                value={
                  termsOfDeliveryOptions.find(
                    (o) => o.value === invoice.terms_of_delivery
                  ) || null
                }
                onChange={(opt) =>
                  onFieldChange("terms_of_delivery", opt ? opt.value : "")
                }
                placeholder="انتخاب شرایط تحویل"
              />
            </div>

            {/* وسیله حمل */}
            <div className={groupClass}>
              <label htmlFor="means_of_transport" className={labelClass}>
                وسیله حمل
              </label>
              <Select
                inputId="means_of_transport"
                classNamePrefix="rs"
                styles={selectStyles}
                options={meansOfTransportOptions}
                isMulti
                value={toMultiValue(
                  invoice.means_of_transport,
                  meansOfTransportOptions
                )}
                onChange={(opts) =>
                  onFieldChange("means_of_transport", fromMultiValue(opts))
                }
                placeholder="انتخاب وسیله حمل"
              />
            </div>

            {/* گمرک مقصد */}
            <div className={groupClass}>
              <label htmlFor="relevant_location" className={labelClass}>
                گمرک مقصد
              </label>
              <Select
                inputId="relevant_location"
                classNamePrefix="rs"
                styles={selectStyles}
                options={iranCustomsOptions}
                isMulti
                value={toMultiValue(
                  invoice.relevant_location,
                  iranCustomsOptions
                )}
                onChange={(opts) =>
                  onFieldChange("relevant_location", fromMultiValue(opts))
                }
                placeholder="انتخاب گمرک مقصد"
              />
            </div>
          </div>
        </div>

        {/* Card footer */}
        <div className="border-t px-4 md:px-6 py-4 text-xs text-gray-500">
          تمام فیلدها در صورت نیاز قابل ویرایش هستند.
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetails;
